import React, { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import { Box, Stack, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

type CameraType = 'front' | 'back' | 'unknown';

type QrScannerProps = {
  onScan: (result: string) => void;
  onError?: (error: unknown) => void;
};

const QRScanner = ({ onScan, onError }: QrScannerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);

  const [cameras, setCameras] = useState<
    {
      id: string;
      label: string;
      type: CameraType;
    }[]
  >([]);

  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);

  const detectCameraType = (label: string): CameraType => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('back') || lowerLabel.includes('environment')) return 'back';
    if (lowerLabel.includes('front') || lowerLabel.includes('user')) return 'front';
    return 'unknown';
  };

  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const cameraList = await QrScanner.listCameras(true);

        const { backCamera, frontCamera, fallbackCamera } = cameraList.reduce(
          (acc, cam) => {
            const type = detectCameraType(cam.label);
            if (type === 'back' && !acc.backCamera) return { ...acc, backCamera: cam };
            if (type === 'front' && !acc.frontCamera) return { ...acc, frontCamera: cam };
            if (!acc.fallbackCamera) return { ...acc, fallbackCamera: cam };
            return acc;
          },
          { backCamera: null, frontCamera: null, fallbackCamera: null } as {
            backCamera: QrScanner.Camera | null;
            frontCamera: QrScanner.Camera | null;
            fallbackCamera: QrScanner.Camera | null;
          },
        );

        const prioritizedCameras = [
          backCamera,
          frontCamera,
          !backCamera && !frontCamera ? fallbackCamera : null,
        ]
          .filter((cam): cam is QrScanner.Camera => cam !== null)
          .map((cam) => ({
            id: cam.id,
            label: cam.label,
            type: detectCameraType(cam.label),
          }));

        setCameras(prioritizedCameras);

        const bestCameraId = prioritizedCameras.length > 0 ? prioritizedCameras[0].id : null;
        setSelectedCamera(bestCameraId);
      } catch (fetchError) {
        console.error('Failed to access cameras. Please check permissions.', fetchError);
        onError?.(fetchError);
      }
    };

    fetchCameras();
  }, []);

  useEffect(() => {
    if (!videoRef.current || !selectedCamera) return;

    const videoElement = videoRef.current;

    const initializeScanner = async () => {
      try {
        if (scannerRef.current) {
          await scannerRef.current.stop();
          await scannerRef.current.setCamera(selectedCamera);
          await scannerRef.current.start();
          return;
        }

        scannerRef.current = new QrScanner(
          videoElement,
          (result: QrScanner.ScanResult) => onScan(result.data),
          {
            highlightScanRegion: true,
            highlightCodeOutline: true,
            returnDetailedScanResult: true,
          },
        );

        if (theme.palette.mode === 'dark') {
          scannerRef.current.setInversionMode('invert');
        }

        await scannerRef.current.start();
        await scannerRef.current.setCamera(selectedCamera);
      } catch (initError) {
        console.error('Scanner initialization failed.', initError);
        onError?.(initError);
      }
    };

    initializeScanner();

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop();
      }
    };
  }, [selectedCamera, onScan, onError, theme.palette.mode]);

  return (
    <Stack spacing={2} alignItems="center">
      <Box position="relative" width="34vh" height="34vh">
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
          }}
        />
      </Box>
    </Stack>
  );
};

export default QRScanner;
